import { z } from 'zod';
import { CallerMessageKind, CallOutcome, TranscriptRole, UnmetRequestKind } from '../enums.js';
import { TimestampSchema } from '../primitives.js';
import { CallConfigMetaSchema, CallFinalizationSchema, EngineUsageSchema } from './callMeta.js';
/**
 * Structured record of one assistant tool call and its result, carried on a
 * `role: 'tool'` timeline entry. `args`/`result` are JSON-serialized and
 * length-capped: they can carry customer PII (name/phone), same as the spoken
 * transcript, and slot lists get long — so we bound what we persist.
 */
export const TranscriptToolSchema = z.object({
    name: z.string().max(100),
    args: z.string().max(2_000).default(''),
    result: z.string().max(4_000).default(''),
    /** Whether the tool reported success (`result.ok`), or null when N/A. */
    ok: z.boolean().nullable().default(null),
});
/** A single timeline entry. `t` = seconds from call start. Spoken turns
 * (assistant/customer) carry `text`; `tool` entries carry `tool`. */
export const TranscriptTurnSchema = z.object({
    role: TranscriptRole,
    text: z.string().default(''),
    t: z.number().min(0),
    /** Present only when `role === 'tool'`. */
    tool: TranscriptToolSchema.optional(),
    /** Assistant turn the caller interrupted: `text` is what was GENERATED, and
     * only part of it was heard (doc 33 W5 #6). Absent = played in full or
     * unknown (records before this field). */
    interrupted: z.boolean().optional(),
    /** Who produced an assistant turn (doc 48): `gateway` = application speech
     * of known text (a read-back, our goodbye). Absent = the model. */
    source: z.enum(['model', 'gateway']).optional(),
    /** Model text whose AUDIO was suppressed while gateway speech owned the
     * line: generated, never heard. */
    suppressed: z.boolean().optional(),
});
/** Maximum turns persisted per call (LLD §4.6). */
export const MAX_TRANSCRIPT_TURNS = 200;
/**
 * Something a caller asked for that the assistant could not do (doc 23 P0).
 *
 * The prompt's catch-all is "if asked anything outside booking, politely
 * redirect" — which is a silent failure by construction: the caller wanted
 * something, did not get it, and nothing anywhere counts it. These rows are
 * the counter. They are what tells us whether the next thing to build is
 * message-taking, opening-hours answers, or price questions, instead of us
 * guessing from the roadmap.
 *
 * Self-reported by the model through `note_unmet_request`, so treat the volume
 * as a signal and the wording as a paraphrase — never as a verbatim quote.
 */
export const UnmetRequestSchema = z.object({
    /** Coarse bucket, for counting. `other` is expected to be the biggest at
     *  first; that is itself the finding. */
    kind: UnmetRequestKind,
    /** The caller's request in the model's words, one short line. */
    summary: z.string().max(200).default(''),
    /** Seconds from call start — pairs the row with its place in the transcript. */
    t: z.number().min(0).default(0),
});
/** Bound per call: a caller who asks twenty impossible things is one data
 *  point, not twenty, and the array is written into a single document. */
export const MAX_UNMET_REQUESTS = 10;
/**
 * A message the caller left for the business (doc 28).
 *
 * Distinct from {@link UnmetRequestSchema}, which is a COUNTER: that one is
 * deliberately invisible to the owner and exists to say how often we fell
 * short. This is the thing an owner reads and acts on, and it survives a call
 * that booked nothing — which is exactly the call that today leaves no trace.
 *
 * Distinct from the appointment's `note` as well (doc 28 D1): the note is a
 * logistics line about the booking, this is what the caller wants the business
 * to know or do.
 *
 * Self-reported by the model, so it is a paraphrase and never a quote. Never
 * fed back into a prompt (doc 28 D7) — it is written for a human.
 */
export const CallerMessageSchema = z.object({
    kind: CallerMessageKind,
    /** The caller's problem/question/request in the business's language, one
     *  short paragraph at most. */
    summary: z.string().max(400).default(''),
    /** The caller ASKED to be called back. Not a promise that anyone will —
     *  nothing in the product makes that true (doc 28 D6). */
    callbackRequested: z.boolean().default(false),
    /** Seconds from call start — pairs the message with its place in the transcript. */
    t: z.number().min(0).default(0),
});
/** `businesses/{businessId}/calls/{callSid}` — id is Twilio's CallSid. */
export const CallRecordSchema = z.object({
    /** Caller ID, or null when withheld (CLIR, common in FR/DE). */
    from: z.string().nullable().default(null),
    startedAt: TimestampSchema,
    endedAt: TimestampSchema.optional(),
    durationSec: z.number().int().min(0).optional(),
    outcome: CallOutcome.optional(),
    appointmentId: z.string().nullable().default(null),
    transcript: z.array(TranscriptTurnSchema).max(MAX_TRANSCRIPT_TURNS).default([]),
    error: z.string().nullable().default(null),
    /** True for browser "Test call" sessions (no real telephony). Real phone
     * calls omit it, so all existing/live records default to false. */
    test: z.boolean().default(false),
    /** What the caller wanted that we could not give them (doc 23 P0). Empty on
     *  every call that stayed inside the booking script — and on every record
     *  written before this existed, which is why it is defaulted. */
    unmetRequests: z.array(UnmetRequestSchema).max(MAX_UNMET_REQUESTS).default([]),
    /** What the caller asked us to pass on (doc 28). One per call: a second
     *  `take_message` is the model revising its summary, not a second message
     *  (doc 28 D4). Null on every call where nothing was left. */
    message: CallerMessageSchema.nullable().default(null),
    /**
     * Every appointment this call CREATED and still holds (doc 33 W2/W7). A call
     * can book more than one (doc 32 F1). `appointmentId` stays as the legacy
     * single link — the first of these — so records and readers older than this
     * field keep rendering; a record without it reads as `[]` or, when only the
     * legacy field exists, as `[appointmentId]` (see {@link callAppointmentIds}).
     */
    appointmentIds: z.array(z.string()).default([]),
    /** Existing appointments this call cancelled (not counting moves). */
    cancelledAppointmentIds: z.array(z.string()).default([]),
    /** Completion marker (doc 33 W7 #9). Absent on records written before it:
     * those were always written once, at the end, and read as final. */
    finalization: CallFinalizationSchema.optional(),
    /** What the conversation ran under (doc 33 W9 #6). */
    config: CallConfigMetaSchema.optional(),
    /** The transcript hit its turn or byte bound (doc 33 W11 #8). */
    transcriptTruncated: z.boolean().default(false),
    /** Model usage and its cost (doc 47 §8). Absent on older records. */
    engineUsage: EngineUsageSchema.optional(),
});
/** All appointment ids a call record links to, legacy records included. */
export function callAppointmentIds(record) {
    if (record.appointmentIds.length > 0)
        return record.appointmentIds;
    return record.appointmentId ? [record.appointmentId] : [];
}
/** Whether consumers should treat this record as the final word on the call.
 * A record with no marker predates it and was only ever written at the end. */
export function isFinalCallRecord(record) {
    if (record.finalization)
        return record.finalization.final;
    return Boolean(record.outcome);
}
export const CallRecordWithIdSchema = CallRecordSchema.extend({ id: z.string() });
/**
 * One setup-wizard demo call, stored for analytics only (doc 13a §6a).
 *
 * Deliberately a SEPARATE top-level collection rather than a flag on
 * `calls/{callSid}`: the promise made to the owner is that trying the assistant
 * out leaves nothing in their business — no entry in call history, nothing in
 * the KPI strip, nothing a customer could be confused with. Keeping it outside
 * `businesses/{id}` is what makes that structural instead of a filter every
 * future query has to remember.
 *
 * It carries the WHOLE conversation, tool calls included, because the questions
 * it has to answer are "where do demos fall apart" and "what do owners ask the
 * assistant before they trust it" — and a transcript without the tool turns
 * cannot tell you that the booking failed on an argument.
 *
 * Server-write-only and never read by the portal (firestore.rules denies both
 * directions to clients).
 */
export const DemoCallSchema = z.object({
    /** The owner who ran it — analytics is per person as much as per business. */
    ownerUid: z.string().min(1),
    businessId: z.string().min(1),
    /** Business language at the time, so a transcript can be read in context. */
    language: z.string().max(10).default(''),
    startedAt: TimestampSchema,
    endedAt: TimestampSchema,
    durationSec: z.number().int().min(0).default(0),
    /** Same shape as a real call's, tool turns included. */
    transcript: z.array(TranscriptTurnSchema).max(MAX_TRANSCRIPT_TURNS).default([]),
    /** Why it ended: the caller hung up, or the demo's own time limit. */
    endedBy: z.enum(['user', 'timeout', 'error']).default('user'),
    createdAt: TimestampSchema,
});
/** How long a demo call may run before the browser ends it (doc 13a §6a). */
export const DEMO_CALL_MAX_SEC = 300;
//# sourceMappingURL=call.js.map